import { useEffect, useReducer } from 'react';
import { useGetCommunityQuery } from 'generated/graphql';
import {
  COMMUNITY_UNREAD_CHANGED_EVENT,
  useCommunityIndicator,
} from '@client/new/hooks/use-community-indicator';
import auth from '@client/components/auth';

const POLL_INTERVAL_MS = 60_000;

export const useCommunityNotificationsCount = (communityId: string) => {
  const { getLastSeenPostId, isCommunityVisited } = useCommunityIndicator();
  const [, recompute] = useReducer((tick: number) => tick + 1, 0);
  const lastSeenPostId = getLastSeenPostId();
  const communityVisited = isCommunityVisited();
  const isAdminModerator = auth.isAdministrator() || auth.isModerator();
  const skip = isAdminModerator || !lastSeenPostId;

  const { data, refetch } = useGetCommunityQuery({
    variables: { id: +communityId },
    skip,
    fetchPolicy: 'network-only',
    pollInterval: POLL_INTERVAL_MS,
  });

  useEffect(() => {
    const onUnreadChanged = () => {
      recompute();
      if (!skip) refetch();
    };
    window.addEventListener(COMMUNITY_UNREAD_CHANGED_EVENT, onUnreadChanged);
    return () =>
      window.removeEventListener(
        COMMUNITY_UNREAD_CHANGED_EVENT,
        onUnreadChanged,
      );
  }, [skip, refetch]);

  if (isAdminModerator) return 0;
  if (!communityVisited) return 1;

  const newerPostsCount = data?.community?.posts
    ?.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .findIndex((post) => post.id === lastSeenPostId);

  return newerPostsCount === undefined || newerPostsCount < 0
    ? 0
    : newerPostsCount;
};
